import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { BodyScrollOptions, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { clamp } from '../../utils/clamp';

export type Options = {
  ref: MutableRefObject<HTMLElement | null>;
  /**
   * Меняет управление блокировкой скролла с автоматического(на mount или unmount)
   * на ручное
   */
  locked?: boolean;
} & BodyScrollOptions;

export function useBodyScrollLock(options: Options) {
  const { ref, locked, allowTouchMove, reserveScrollBarGap = true }: Options = options;
  const refsLocks = useRef<HTMLElement[]>([]);

  const lock = useCallback(
    (currentRef: HTMLElement) => {
      refsLocks.current.push(currentRef);
      lockBody(currentRef, { allowTouchMove, reserveScrollBarGap });
    },
    [allowTouchMove, reserveScrollBarGap]
  );

  const unlock = useCallback((currentRef: HTMLElement) => {
    refsLocks.current = refsLocks.current.filter(currentLockedRef => currentLockedRef !== currentRef);
    unlockBody(currentRef);
  }, []);

  useEffect(() => {
    const { current } = ref;

    if (current && typeof locked !== 'undefined') {
      if (locked) {
        lock(current);

        return () => {
          unlock(current);
        };
      }
    }

    return () => {};
  }, [locked, ref, lock, unlock]);

  useEffect(() => {
    const { current } = ref;

    if (current) {
      if (typeof locked === 'undefined') {
        lock(current);

        return () => {
          unlock(current);
        };
      }
    }

    return () => {};
  }, [ref, locked, lock, unlock]);

  useEffect(() => () => refsLocks.current.forEach(unlockBody), []);
}

let cachedScrollTop: null | number = null;
let modalsCount = 0;

function lockBody(ref: HTMLElement, bodyScrollLockOptions: BodyScrollOptions) {
  disableBodyScroll(ref, bodyScrollLockOptions);

  if (modalsCount === 0) {
    if (document.scrollingElement) {
      const { scrollTop } = document.scrollingElement;
      cachedScrollTop = scrollTop;
    }

    modalsCount += 1;

    setTimeout(() => {
      if (modalsCount > 0) {
        if (document.scrollingElement) document.scrollingElement.scrollTop = 0;

        if (cachedScrollTop) {
          document.documentElement.style.top = `${-cachedScrollTop}px`;
        }
        document.documentElement.style.position = 'fixed';
        document.documentElement.style.left = '0';
        document.documentElement.style.right = '0';
      }
    }, 0);
  }
}

function unlockBody(ref: HTMLElement) {
  enableBodyScroll(ref);

  modalsCount = clamp(0, modalsCount - 1, Infinity);

  if (modalsCount === 0) {
    document.documentElement.style.position = '';
    document.documentElement.style.top = '';
    document.documentElement.style.left = '';

    document.documentElement.style.right = '';
    if (document.scrollingElement && cachedScrollTop) document.scrollingElement.scrollTop = cachedScrollTop;
    cachedScrollTop = null;
  }
}
